import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useUpdateEnterpriseDataMutation } from '@/store/api/Enterprise/Enterprise.query'
import { useUserInfoQuery } from '@/store/api/Users/Users.query'
import type { enterpriseType } from '@/utils/schemas'
import { enterpriseSchema } from '@/utils/schemas'

export const Enterprise = () => {
  const { t } = useTranslation()
  const { data, isLoading: isLoadingUserInfo } = useUserInfoQuery()

  const [updateEnterpriseData, { isLoading: isLoadingUpdateData }] =
    useUpdateEnterpriseDataMutation()

  const form = useForm<enterpriseType>({
    resolver: zodResolver(enterpriseSchema),
    defaultValues: {
      companyName: data?.enterprise.name || '',
      companyAddress: data?.enterprise.address || '',
    },
    mode: 'onChange',
  })
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form

  const handleUpdateEnterpriseData = async (formData: enterpriseType) => {
    try {
      if (data?.enterprise.name) {
        await updateEnterpriseData({
          enterpriseId: data?.enterprise.id,
          companyName:
            formData.companyName === data?.enterprise.name
              ? undefined
              : formData.companyName,
          companyAddress:
            formData.companyAddress === data?.enterprise.address
              ? undefined
              : formData.companyAddress,
        }).unwrap()
        toast.success(t('feedback.toast.changesSaved'))
      }
    } catch (error) {
      toast.error(t('feedback.toast.errorSavingChanges'))
      catchWithSentry(error)
    }
  }

  if (isLoadingUserInfo) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LottieSpinner width={120} height={120} />
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(handleUpdateEnterpriseData)}
        className="flex flex-1 min-h-0 flex-col"
      >
        <div className="flex flex-1 min-h-0 flex-col gap-8 overflow-y-auto">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-neutralColor">
              {t('dashboard.account.enterprise.companySettings')}
            </h2>
            <p className="text-sm text-neutralColor60">
              {t('dashboard.account.enterprise.companySubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={
                        data?.enterprise.name ||
                        t('dashboard.account.enterprise.companyName')
                      }
                      className="h-11 rounded-lg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="companyAddress"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={
                        data?.enterprise.address ||
                        t('dashboard.account.enterprise.noAddressAvailable')
                      }
                      className="h-11 rounded-lg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex shrink-0 justify-end pt-6">
          <Button
            type="submit"
            disabled={!isDirty}
            isLoading={isLoadingUpdateData}
            className="self-end"
          >
            {t('dashboard.account.enterprise.save')}
          </Button>
        </div>
      </form>
    </Form>
  )
}
